/*
 * Recently viewed — browser-based ("Pick up where you left off") product
 * history for the AlCell theme.
 *
 * Two responsibilities, both driven by markup so no per-page glue is needed:
 *
 *   1. TRACK  — on a product page, snippets/recently-viewed-track.liquid emits a
 *      <script type="application/json" data-rv-current> snapshot of the current
 *      product. We unshift it (most-recent-first, deduped) into localStorage.
 *
 *   2. RENDER — on the homepage, sections/recently-viewed.liquid ships a hidden,
 *      empty carousel shell ([data-rv-root] with a [data-rv-track]). We rebuild
 *      cards from localStorage into the track and reveal the section. The card
 *      markup mirrors snippets/featured-tabs-card.liquid exactly, so the shared
 *      section-featured-tabs.css/.js (arrows, mobile swipe, hover controls) and
 *      the global wishlist.js delegation (heart + fast Add-to-Cart) all apply.
 *
 * Storage shape (localStorage key "alcell:recently-viewed"), newest first:
 *   [{ id, handle, title, url, image, price, compare, available, variant, new }]
 *   price/compare are integer cents (Shopify money), formatted on render.
 *
 * It works for guests and logged-in customers, needs no app/backend, and stays
 * on-device (no cross-device sync — the same metafield/app upgrade path noted in
 * wishlist.js applies here too).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'alcell:recently-viewed';
  var STORE_LIMIT = 20; // keep a few more than we show, so the cap never starves the view

  /* ---------- storage ---------- */

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full / disabled — fail silently */
    }
  }

  // Move/insert `item` to the front, deduped by id, capped at STORE_LIMIT.
  function record(item) {
    if (!item || item.id == null) return;
    var list = read().filter(function (i) {
      return String(i.id) !== String(item.id);
    });
    list.unshift(item);
    write(list.slice(0, STORE_LIMIT));
  }

  /* ---------- helpers ---------- */

  // Format integer cents as "R 1,234" — mirrors the price-rand Liquid snippet
  // (whole rands, comma thousands separators, no decimals).
  function formatRand(cents) {
    var rands = Math.floor((parseInt(cents, 10) || 0) / 100);
    return 'R ' + String(rands).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---------- track (product pages) ---------- */

  function trackCurrent() {
    var el = document.querySelector('[data-rv-current]');
    if (!el) return;
    var data;
    try {
      data = JSON.parse(el.textContent);
    } catch (e) {
      return;
    }
    record(data);
  }

  /* ---------- render (homepage carousel) ---------- */

  // Rebuild a single card. Mirrors snippets/featured-tabs-card.liquid so it
  // inherits all of section-featured-tabs.css and the wishlist.js delegation.
  function cardHTML(item) {
    var price = parseInt(item.price, 10) || 0;
    var compare = parseInt(item.compare, 10) || 0;
    var onSale = compare > price;

    var badge = '';
    if (item['new']) {
      badge = '<span class="ftabs__badge ftabs__badge--new">NEW</span>';
    } else if (onSale) {
      // Integer division to match the Liquid badge (… | times: 100 | divided_by: compare).
      var pct = Math.floor((compare - price) * 100 / compare);
      badge = '<span class="ftabs__badge ftabs__badge--sale">-' + pct + '%</span>';
    }

    var media = item.image
      ? '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" class="ftabs__img" loading="lazy">'
      : '<div class="ftabs__img ftabs__img--placeholder"></div>';

    var priceRow = onSale
      ? '<span class="ftabs__price ftabs__price--sale">' + esc(formatRand(price)) + '</span>' +
        '<span class="ftabs__price ftabs__price--compare">' + esc(formatRand(compare)) + '</span>'
      : '<span class="ftabs__price">' + esc(formatRand(price)) + '</span>';

    var multi = ''; // recently-viewed only stores the first variant; treat as single-add
    var atc = item.available
      ? '<a href="' + esc(item.url) + '" class="ftabs__atc" tabindex="-1"' +
          ' aria-label="Add ' + esc(item.title) + ' to cart" data-card-atc' +
          ' data-url="' + esc(item.url) + '" data-variant="' + esc(item.variant) + '"' + multi + '>' +
          'Add To Cart' +
          '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>' +
        '</a>'
      : '<span class="ftabs__atc ftabs__atc--oos" aria-hidden="true">Out of Stock</span>';

    return '' +
      '<article class="ftabs__card" tabindex="0">' +
        '<div class="ftabs__media">' +
          '<a href="' + esc(item.url) + '" class="ftabs__media-link" aria-label="' + esc(item.title) + '">' +
            badge + media +
          '</a>' +
          '<button type="button" class="ftabs__wishlist" data-wishlist-toggle' +
            ' data-wishlist-id="' + esc(item.id) + '"' +
            ' data-wishlist-handle="' + esc(item.handle) + '"' +
            ' data-wishlist-title="' + esc(item.title) + '"' +
            ' data-wishlist-url="' + esc(item.url) + '"' +
            ' data-wishlist-image="' + esc(item.image) + '"' +
            ' data-wishlist-price="' + esc(price) + '"' +
            ' data-wishlist-compare="' + esc(compare) + '"' +
            ' data-wishlist-available="' + (item.available ? 'true' : 'false') + '"' +
            ' data-wishlist-variant="' + esc(item.variant) + '"' +
            ' aria-pressed="false" aria-label="Add to wishlist">' +
            '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
          '</button>' +
          atc +
        '</div>' +
        '<div class="ftabs__body">' +
          '<a href="' + esc(item.url) + '" class="ftabs__title-link">' +
            '<p class="ftabs__title">' + esc(item.title) + '</p>' +
          '</a>' +
          '<div class="ftabs__price-row">' + priceRow + '</div>' +
        '</div>' +
      '</article>';
  }

  // Fallback / recommended products emitted server-side (data-rv-fallback),
  // used to top up a thin row so it never looks half-empty.
  function readFallback(root) {
    var el = root.querySelector('[data-rv-fallback]');
    if (!el) return [];
    try {
      var list = JSON.parse(el.textContent);
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  // Append items from `pool` not already present (by id) until `list` reaches
  // `target` (or the pool runs out).
  function backfill(list, pool, target) {
    var seen = {};
    list.forEach(function (i) { seen[String(i.id)] = true; });
    for (var k = 0; k < pool.length && list.length < target; k++) {
      var item = pool[k];
      if (item && !seen[String(item.id)]) {
        list.push(item);
        seen[String(item.id)] = true;
      }
    }
  }

  // Verify a stored item still points at a live, in-stock product. Out-of-stock
  // products are unpublished from the Online Store, so their /products/<handle>.js
  // returns 404 (and the product page would 404 on click). Resolve to the item
  // when it's still published AND in stock, otherwise null so it gets pruned. On
  // a network error we KEEP the item rather than nuke history on a transient blip.
  function verifyItem(item) {
    if (!item || !item.handle) return Promise.resolve(null);
    return fetch('/products/' + encodeURIComponent(item.handle) + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) return null; // 404 → unpublished / deleted → drop
      return r.json().then(function (p) {
        if (!p || p.available === false) return null; // sold-out → drop (in-stock only)
        // Refresh the volatile fields so a long-lived card stays accurate.
        item.available = true;
        if (typeof p.price === 'number') item.price = p.price;
        if (typeof p.compare_at_price === 'number') item.compare = p.compare_at_price || 0;
        return item;
      }).catch(function () { return item; });
    }).catch(function () { return item; });
  }

  // Prune the stored history down to live, in-stock products and persist the
  // result, so unpublished / sold-out entries never render as 404-on-click cards.
  function prunedRecent() {
    var original = read();
    if (!original.length) return Promise.resolve([]);
    return Promise.all(original.map(verifyItem)).then(function (results) {
      var kept = results.filter(Boolean);
      if (kept.length !== original.length) write(kept);
      return kept;
    });
  }

  function renderOne(root, recent) {
    var track = root.querySelector('[data-rv-track]');
    if (!track) return;

    var min = parseInt(root.getAttribute('data-rv-min'), 10);
    if (isNaN(min)) min = 6;
    var max = parseInt(root.getAttribute('data-rv-max'), 10) || 12;

    var fallback = readFallback(root);
    var designMode = window.Shopify && window.Shopify.designMode;

    var list;
    if (recent.length) {
      // Real history leads; top up with recommendations only if it's thin.
      list = recent.slice();
      if (list.length < min) backfill(list, fallback, min);
      list = list.slice(0, max);
    } else if (designMode && fallback.length) {
      // No history in the editor — preview the layout with the fallback set.
      list = fallback.slice(0, max);
    } else if (designMode) {
      // No history and no fallback configured: explain rather than show blank.
      track.innerHTML = '<p class="ftabs__empty">Each visitor sees the products they recently viewed here ' +
        '(topped up from the fallback collection). It is empty in the editor because there is no browsing history.</p>';
      root.removeAttribute('hidden');
      return;
    } else {
      // First-time visitor on the live site: stay hidden.
      track.innerHTML = '';
      root.setAttribute('hidden', '');
      return;
    }

    track.innerHTML = list.map(cardHTML).join('');
    root.removeAttribute('hidden');

    // Let the shared wishlist.js sync heart states on the freshly-built cards.
    if (window.Wishlist && typeof window.Wishlist.refresh === 'function') {
      window.Wishlist.refresh();
    }
  }

  // Two ordered id-lists equal? Used to skip a needless re-render (and the flicker
  // it would cause) when background verification didn't actually change anything.
  function sameOrder(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (String(a[i].id) !== String(b[i].id)) return false;
    }
    return true;
  }

  // Render immediately from localStorage, then verify in the background. The old
  // flow network-verified every stored product BEFORE revealing, which left the
  // homepage without this row for a beat and then popped it in above the fold —
  // the visible "flash". Instead we:
  //   1. render straight from the stored (already last-verified) history so the
  //      carousel appears the instant this script runs — replacing the pre-painted
  //      skeleton cards seamlessly (same row height, no layout shift);
  //   2. re-verify in the background and re-render ONLY if a product has since gone
  //      unpublished / out of stock, so the common "nothing changed" case never
  //      flickers. Stored history is pruned on every visit, so the only cards that
  //      can briefly be stale are ones that changed since the last page load.
  function renderRoots() {
    var roots = document.querySelectorAll('[data-rv-root]');
    if (!roots.length) return;

    var optimistic = read();
    roots.forEach(function (root) { renderOne(root, optimistic); });

    prunedRecent().then(function (pruned) {
      if (sameOrder(pruned, optimistic)) return;
      roots.forEach(function (root) { renderOne(root, pruned); });
    });
  }

  /* ---------- init ---------- */

  function init() {
    trackCurrent(); // no-op when not on a product page
    renderRoots();  // no-op when no carousel on the page
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-render when the section reloads in the theme editor.
  document.addEventListener('shopify:section:load', renderRoots);

  // Keep the carousel in sync if another tab records a new view.
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) renderRoots();
  });
})();
